"use strict";
/**
 * Weekly Report V2 — Detail Response Types
 *
 * Zod schemas for pre-computed detail screens stored in wr_detail JSONB.
 * Each detail page (product, treatment, metric, suggestion) is fully computed
 * at report generation time. The tRPC endpoints are simple JSONB fetches.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.LifestyleDetailDataSchema = exports.SuggestionDetailDataSchema = exports.ScheduleSlotSchema = exports.MetricDetailDataSchema = exports.RelatedTreatmentSchema = exports.RelatedProductSchema = exports.RegionDeepDiveSchema = exports.MetricRegionSchema = exports.TreatmentDetailDataSchema = exports.TreatmentPhaseSchema = exports.DISPLAY_PHASE_ORDER = exports.DISPLAY_PHASE_MAP = exports.DisplayPhaseName = exports.ProductDetailDataSchema = exports.ProductChangeSchema = void 0;
const zod_1 = require("zod");
const enums_1 = require("../api/enums");
const sectionContent_1 = require("./sectionContent");
// ============================================================================
// PRODUCT DETAIL
// ============================================================================
/** A routine change event on a product timeline */
exports.ProductChangeSchema = zod_1.z.object({
    description: zod_1.z.string(),
    detail: zod_1.z.string(),
    weeksAgo: zod_1.z.number(),
    type: zod_1.z.enum(['frequency_change', 'added', 'removed', 'consistency_change']),
    /** ISO date string of the change event. Present on reports generated after
     *  the schema update; absent on older stored rows (fall back to weeksAgo). */
    changedAt: zod_1.z.string().optional(),
});
exports.ProductDetailDataSchema = zod_1.z.object({
    productName: zod_1.z.string(),
    brandName: zod_1.z.string().nullable(),
    ingredientCategory: zod_1.z.string(),
    addedDate: zod_1.z.string(),
    // Observation window
    observationWindowWeeks: zod_1.z.number(),
    weeksElapsed: zod_1.z.number(),
    totalWeeks: zod_1.z.number(),
    startDate: zod_1.z.string(),
    endDate: zod_1.z.string(),
    observationProse: zod_1.z.string(),
    milestoneReached: zod_1.z.boolean(),
    // Routine changes
    changes: zod_1.z.array(exports.ProductChangeSchema),
    // Narrative
    leadInProse: zod_1.z.string(),
    // Metrics
    primaryMetrics: zod_1.z.array(sectionContent_1.DetailMetricChartSchema),
    adjustmentNote: zod_1.z
        .object({
        headline: zod_1.z.string(),
        body: zod_1.z.string(),
    })
        .nullable(),
    secondaryMetrics: zod_1.z.array(sectionContent_1.UniversalMetricTileSchema),
    // Editable details
    frequency: zod_1.z.string().nullable(),
    timeOfDay: zod_1.z.string().nullable(),
    consistency: zod_1.z.string().nullable(),
    // Routine item reference (for edit/remove actions)
    routineItemId: zod_1.z.number(),
    // Live state (merged at query time, not pre-computed)
    hasRoutineChangedSinceReport: zod_1.z.boolean().optional(),
});
// ============================================================================
// TREATMENT DETAIL
// ============================================================================
/** Simplified display phases for the UI (mapped from 6 physiological phases) */
exports.DisplayPhaseName = {
    ONSET: 'onset',
    FULL_EFFECT: 'full_effect',
    RETURN: 'return',
};
/** Maps each physiological treatment response phase to its display group */
exports.DISPLAY_PHASE_MAP = {
    acute: 'onset',
    proliferative: 'onset',
    presentation: 'onset',
    maturation: 'full_effect',
    plateau: 'full_effect',
    waning: 'return',
};
/** Display phase iteration order */
exports.DISPLAY_PHASE_ORDER = [
    exports.DisplayPhaseName.ONSET,
    exports.DisplayPhaseName.FULL_EFFECT,
    exports.DisplayPhaseName.RETURN,
];
/** A phase in the treatment response timeline (3 display phases) */
exports.TreatmentPhaseSchema = zod_1.z.object({
    name: zod_1.z.enum(['onset', 'full_effect', 'return']),
    weekRange: zod_1.z.string(),
    isCurrent: zod_1.z.boolean(),
    startWeek: zod_1.z.number(),
    endWeek: zod_1.z.number(),
});
exports.TreatmentDetailDataSchema = zod_1.z.object({
    treatmentId: zod_1.z.number(),
    treatmentSlug: zod_1.z.enum(enums_1.treatmentSlugEnum),
    treatmentName: zod_1.z.string(),
    treatmentType: zod_1.z.string(),
    areas: zod_1.z.array(sectionContent_1.AreaSchema),
    treatmentDate: zod_1.z.string(),
    weekNumber: zod_1.z.number(),
    // Observation window
    observationWindowWeeks: zod_1.z.number(),
    weeksElapsed: zod_1.z.number(),
    observationProse: zod_1.z.string(),
    milestoneReached: zod_1.z.boolean(),
    // Response timeline
    phases: zod_1.z.array(exports.TreatmentPhaseSchema),
    currentWeek: zod_1.z.number(),
    timelineProse: zod_1.z.string(),
    // Narrative
    leadInProse: zod_1.z.string(),
    // Metrics
    targetMetrics: zod_1.z.array(sectionContent_1.DetailMetricChartSchema),
    secondaryMetrics: zod_1.z.array(sectionContent_1.UniversalMetricTileSchema),
    // Editable details
    provider: zod_1.z.string().nullable(),
});
// ============================================================================
// METRIC DETAIL
// ============================================================================
/** A region in the regional breakdown */
exports.MetricRegionSchema = zod_1.z.object({
    regionName: zod_1.z.string(),
    area: sectionContent_1.AreaSchema,
    score: zod_1.z.number(),
    percentChange: zod_1.z.number().nullable(),
    trend: sectionContent_1.TrendDirectionSchema,
    sparklinePoints: zod_1.z.array(zod_1.z.number()),
    isLeading: zod_1.z.boolean(),
});
/** Deep dive into the most notable region */
exports.RegionDeepDiveSchema = zod_1.z.object({
    regionName: zod_1.z.string(),
    area: sectionContent_1.AreaSchema,
    baselineScore: zod_1.z.number().nullable(),
    currentScore: zod_1.z.number(),
    percentChange: zod_1.z.number().nullable(),
    trend: sectionContent_1.TrendDirectionSchema,
    timeSeries: zod_1.z.array(sectionContent_1.TimeSeriesPointSchema),
    observationProse: zod_1.z.string(),
});
/** A product related to a metric via MECHANISM_MATRIX reverse lookup */
exports.RelatedProductSchema = zod_1.z.object({
    productId: zod_1.z.number(),
    productName: zod_1.z.string(),
    headlineStatus: zod_1.z.string(),
    routineNote: zod_1.z.string().nullable(),
});
/** A treatment related to a metric via target metric lookup */
exports.RelatedTreatmentSchema = zod_1.z.object({
    treatmentId: zod_1.z.number(),
    treatmentSlug: zod_1.z.enum(enums_1.treatmentSlugEnum),
    treatmentName: zod_1.z.string(),
    treatmentType: zod_1.z.string(),
    headlineStatus: zod_1.z.string(),
});
exports.MetricDetailDataSchema = zod_1.z.object({
    groupName: zod_1.z.string(),
    metricName: zod_1.z.string(),
    techName: sectionContent_1.TechNameSchema,
    description: zod_1.z.string(),
    // Full-face overview
    score: zod_1.z.number(),
    trend: sectionContent_1.TrendDirectionSchema,
    fullFaceTimeSeries: zod_1.z.array(sectionContent_1.TimeSeriesPointSchema),
    baselineScore: zod_1.z.number().nullable(),
    confidenceTier: zod_1.z.enum([
        'limited',
        'emerging',
        'building',
        'moderate',
        'strong',
    ]),
    // Regional breakdown
    regions: zod_1.z.array(exports.MetricRegionSchema),
    regionDeepDive: exports.RegionDeepDiveSchema.nullable(),
    // Routine context (MECHANISM_MATRIX reverse lookup)
    relatedProducts: zod_1.z.array(exports.RelatedProductSchema),
    relatedTreatments: zod_1.z.array(exports.RelatedTreatmentSchema),
    aboutDescription: zod_1.z.string(),
});
// ============================================================================
// SUGGESTION DETAIL (3 visual variants, 1 schema)
// ============================================================================
const PeriodSchema = zod_1.z.enum(enums_1.periodEnum);
exports.ScheduleSlotSchema = zod_1.z.object({
    day: zod_1.z.enum(enums_1.dayOfWeekEnum),
    period: PeriodSchema,
});
exports.SuggestionDetailDataSchema = zod_1.z.object({
    // Identity
    suggestionId: zod_1.z.number(),
    suggestionType: zod_1.z.string(),
    title: zod_1.z.string(),
    subtitle: zod_1.z.string(),
    ingredientCategory: zod_1.z.string().optional(),
    // Product image (for product-related suggestions; null for add_ingredient)
    productImageUrl: zod_1.z.string().nullable(),
    // Target metric tiles affected by this suggestion (MetricTileStrip display)
    targetMetricTiles: zod_1.z.array(sectionContent_1.UniversalMetricTileSchema),
    // Variant: Schedule Comparison (frequency types only, null otherwise)
    scheduleComparison: zod_1.z
        .object({
        currentFrequency: zod_1.z.number(),
        currentUnit: zod_1.z.string(),
        currentTimeOfDay: zod_1.z.array(PeriodSchema),
        currentSlots: zod_1.z.array(exports.ScheduleSlotSchema),
        suggestedFrequency: zod_1.z.number(),
        suggestedUnit: zod_1.z.string(),
        suggestedTimeOfDay: zod_1.z.array(PeriodSchema),
        suggestedSlots: zod_1.z.array(exports.ScheduleSlotSchema),
    })
        .nullable(),
    // Variant: Recommended Schedule (add_ingredient only, null otherwise)
    recommendedSchedule: zod_1.z
        .object({
        frequency: zod_1.z.number(),
        timeOfDay: zod_1.z.array(PeriodSchema),
        slots: zod_1.z.array(exports.ScheduleSlotSchema),
    })
        .nullable(),
    // Variant: Product Removal (simplification only, null otherwise)
    productToRemove: zod_1.z
        .object({
        productName: zod_1.z.string(),
        brandName: zod_1.z.string().nullable(),
        imageUrl: zod_1.z.string().nullable(),
        frequency: zod_1.z.string(),
        timeOfDay: zod_1.z.array(PeriodSchema),
        overlapExplanation: zod_1.z.string(),
    })
        .nullable(),
    // Rationale (two-track composed)
    metricContext: zod_1.z.string().nullable(),
    rationale: zod_1.z.string(),
    // Common prose (LLM-generated via Stage 2 Call B)
    whatWouldChange: zod_1.z.string(),
    whatStellaWillObserve: zod_1.z.array(zod_1.z.string()).length(3),
    expectedTimeline: zod_1.z.string(),
    // Product recommendations (add_ingredient only, empty array otherwise)
    productRecommendations: zod_1.z.array(sectionContent_1.SuggestionProductRecommendationSchema),
    // CTA
    ctaType: zod_1.z.enum(['apply_schedule', 'product_search', 'remove_product']),
    // Live state (merged at query time, not pre-computed)
    acceptedAt: zod_1.z.string().nullable(),
});
// ============================================================================
// LIFESTYLE DETAIL
// ============================================================================
exports.LifestyleDetailDataSchema = zod_1.z.object({
    // ── Signal identity ──
    /** Signal key (e.g., 'sleep', 'activity') */
    signal: zod_1.z.string(),
    /** Display name (e.g., 'Sleep', 'Activity') */
    displayName: zod_1.z.string(),
    /** Medical band position */
    medicalBand: zod_1.z.enum(['low', 'reduced', 'normal', 'elevated']),
    /** Deviation from baseline (null if no baseline) */
    deviationPercent: zod_1.z.number().nullable(),
    /** Direction of change */
    direction: zod_1.z.enum(['declining', 'stable', 'rising']),
    /** Acute window median */
    acuteMedian: zod_1.z.number(),
    /** Baseline median (null if insufficient data) */
    baselineMedian: zod_1.z.number().nullable(),
    /** Unit (e.g., 'minutes', 'steps', 'ms') */
    unit: zod_1.z.string(),
    // ── Daily values for chart ──
    /** 7-day daily values for chart rendering */
    dailyValues: zod_1.z.array(zod_1.z.object({
        date: zod_1.z.string(),
        value: zod_1.z.number(),
    })),
    /** Supporting context values (e.g., Quality: 82%, Exercise: 27 min) */
    supportingValues: zod_1.z
        .array(zod_1.z.object({
        label: zod_1.z.string(),
        value: zod_1.z.number(),
        unit: zod_1.z.string(),
    }))
        .optional(),
    // ── Prose ──
    /** Per-bucket LLM-generated narrative */
    bucketProse: zod_1.z.string().nullable(),
    /** Bridge text from medical reference bands */
    skinContext: zod_1.z.string(),
    // ── Linked skin metrics (with full tile data from metric statistics) ──
    /** Skin metrics linked to this signal, with scores, sparklines, and trends */
    linkedMetricTiles: zod_1.z.array(sectionContent_1.UniversalMetricTileSchema),
    /** Correlation status per metric (matched = concordant change, watching = no change yet) */
    linkedMetricStatuses: zod_1.z.array(zod_1.z.object({
        metric: sectionContent_1.TechNameSchema,
        status: zod_1.z.enum(['matched', 'watching']),
    })),
    // ── Product context ──
    /** Routine product targeting the same metrics (null if none) */
    productContext: zod_1.z
        .object({
        productName: zod_1.z.string(),
        targetMetric: zod_1.z.string(),
        observationNote: zod_1.z.string(),
    })
        .nullable(),
    /** Products in the user's routine that target the same linked metrics */
    relatedProducts: zod_1.z.array(exports.RelatedProductSchema),
    /** Treatments targeting the same linked metrics */
    relatedTreatments: zod_1.z.array(exports.RelatedTreatmentSchema),
    // ── Curated lifestyle tips ──
    /** Curated lifestyle tips relevant to this signal + user state */
    tips: zod_1.z
        .array(zod_1.z.object({
        factor: zod_1.z.string(),
        action: zod_1.z.string(),
        rationale: zod_1.z.string(),
        evidenceStrength: zod_1.z.enum(['strong', 'moderate', 'emerging']),
        citation: zod_1.z.string(),
    }))
        .optional(),
    // ── Confound note data ──
    /** Metric display names for the confound note */
    linkedMetricNames: zod_1.z.array(zod_1.z.string()),
    // ── Dual-axis correlation chart ──
    /** Weekly paired data points for the lifestyle-vs-skin overlay chart */
    dualAxisSeries: zod_1.z
        .array(zod_1.z.object({
        /** ISO week start date formatted as "MMM D" (e.g., "Mar 3") */
        weekLabel: zod_1.z.string(),
        /** Weekly median for the lifestyle signal (null = no wearable data that week) */
        signalMedian: zod_1.z.number().nullable(),
        /** Weekly score for the correlated skin metric (null = no scan that week) */
        skinMetricScore: zod_1.z.number().nullable(),
    }))
        .default([]),
    /** Top correlation match metadata for chart header/legend */
    topCorrelation: zod_1.z
        .object({
        skinMetric: sectionContent_1.TechNameSchema,
        displayName: zod_1.z.string(),
        direction: zod_1.z.enum(['improvement', 'worsening']),
        confidenceLevel: zod_1.z.enum(['low', 'moderate', 'high']),
        lagDays: zod_1.z.number(),
    })
        .nullable()
        .default(null),
    /** Chart-aware LLM narrative referencing the dual-axis data */
    detailNarrative: zod_1.z.string().nullable().default(null),
});
//# sourceMappingURL=detailData.js.map